import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const projectKeys = {
  byWorkspace: (workspaceSlug: string) => ['projects', 'workspace', workspaceSlug] as const,
  detail: (workspaceSlug: string, projectSlug: string) =>
    ['projects', 'detail', workspaceSlug, projectSlug] as const,
  memberRates: (workspaceSlug: string, projectSlug: string) =>
    ['projects', 'member-rates', workspaceSlug, projectSlug] as const,
};

/**
 * Shared `queryOptions` so the same key + fetcher are used by `useProjects()`
 * (in components) and by route loaders (`queryClient.ensureQueryData(...)`).
 * One source of truth — when the page mounts, the cache hit is automatic.
 */
export const projectsQueryOptions = (api: ApiClient, workspaceSlug: string) =>
  queryOptions({
    queryKey: projectKeys.byWorkspace(workspaceSlug),
    // Project list drives the sidebar — keep it warm so switching
    // workspaces doesn't reflash the project section. Mutations
    // (create / archive) explicitly invalidate this key.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/projects', {
        params: { path: { workspace_slug: workspaceSlug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useProjects = (workspaceSlug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...projectsQueryOptions(api, workspaceSlug ?? ''),
    enabled: Boolean(workspaceSlug),
  });
};

export const projectQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  projectSlug: string,
) =>
  queryOptions({
    queryKey: projectKeys.detail(workspaceSlug, projectSlug),
    // Project metadata (name, description, billing) changes via
    // explicit settings forms which invalidate this key.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useProject = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...projectQueryOptions(api, workspaceSlug ?? '', projectSlug ?? ''),
    enabled: Boolean(workspaceSlug && projectSlug),
  });
};

export interface CreateProjectInput {
  name: string;
  description?: string;
}

export const useCreateProject = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProjectInput) => {
      const { data, error } = await api.POST('/v1/workspaces/{workspace_slug}/projects', {
        params: { path: { workspace_slug: workspaceSlug } },
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: projectKeys.byWorkspace(workspaceSlug) });
    },
  });
};

export type ProjectBillingType = 'none' | 'flat' | 'hourly';

/**
 * Project type — the feature-gating axis (slice 10). `engineering`
 * unlocks GitHub integration UI surfaces (slices 11/12) and the
 * per-task type picker; `generic` is the default and shows the
 * workspace-management defaults only.
 */
export type ProjectType = 'engineering' | 'generic';

export const PROJECT_TYPES: ReadonlyArray<ProjectType> = ['engineering', 'generic'];

export interface UpdateProjectInput {
  name?: string;
  description?: string | null;
  type?: ProjectType;
  /**
   * GitHub repo linkage (slice 11). Both must move together —
   * provide both fields to link, both `null` to unlink. Engineering-
   * typed projects only; server rejects with `repo_not_engineering`
   * otherwise.
   */
  github_repo_owner?: string | null;
  github_repo_name?: string | null;
  lead_user_id?: string | null;
  billing_type?: ProjectBillingType;
  flat_fee_cents?: number | null;
  default_hourly_rate_cents?: number | null;
}

export const useUpdateProject = (workspaceSlug: string, projectSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProjectInput) => {
      // The OpenAPI types model nullable scalars as non-nullable; the Laravel
      // controller accepts `null` to clear `description`, `lead_user_id`,
      // `flat_fee_cents`, and `default_hourly_rate_cents`. Same pattern as
      // useUpdateTask. (Tracked in api/BACKLOG.md.)
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}',
        {
          params: { path: { workspace_slug: workspaceSlug, projectSlug } },
          body: input as {
            name?: string;
            description?: string;
            type?: ProjectType;
            github_repo_owner?: string;
            github_repo_name?: string;
            lead_user_id?: string;
            billing_type?: ProjectBillingType;
            flat_fee_cents?: number;
            default_hourly_rate_cents?: number;
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: projectKeys.detail(workspaceSlug, projectSlug) });
      await qc.invalidateQueries({ queryKey: projectKeys.byWorkspace(workspaceSlug) });
    },
  });
};

export const useProjectMemberRates = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.memberRates(workspaceSlug ?? '', projectSlug ?? ''),
    enabled: Boolean(workspaceSlug && projectSlug),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/member-rates',
        {
          params: {
            path: {
              workspace_slug: workspaceSlug as string,
              projectSlug: projectSlug as string,
            },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });
};

export interface SetProjectMemberRateInput {
  userId: string;
  /** Pass `null` (or omit) to clear the override; integer to set. */
  hourly_rate_cents: number | null;
}

export const useSetProjectMemberRate = (workspaceSlug: string, projectSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetProjectMemberRateInput) => {
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/member-rates/{userId}',
        {
          params: {
            path: {
              workspace_slug: workspaceSlug,
              projectSlug,
              userId: input.userId,
            },
          },
          body: { hourly_rate_cents: input.hourly_rate_cents } as {
            hourly_rate_cents?: number;
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: projectKeys.memberRates(workspaceSlug, projectSlug) });
    },
  });
};
